import { useMemo, useState, useEffect, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { useAuditLogs, useCompanies, useUsers, type AuditLogFilter } from '@/lib/adminApi';
import { relativeTime } from '@/lib/relativeTime';

/**
 * AuditLogPage — /admin/audit-logs (ADM-06 read surface).
 *
 * Filter bar (Company / Actor / Target type / Action / From / To) round-trips
 * through useSearchParams so URLs are shareable. Table is paginated via
 * nextCursor ("Load more" button). Each row expands to show before/after
 * JSON pretty-printed.
 */
export default function AuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [companyId, setCompanyId] = useState(searchParams.get('companyId') ?? '');
  const [actorUserId, setActorUserId] = useState(searchParams.get('actorUserId') ?? '');
  const [targetType, setTargetType] = useState(searchParams.get('targetType') ?? '');
  const [action, setAction] = useState(searchParams.get('action') ?? '');
  const [from, setFrom] = useState(searchParams.get('from') ?? '');
  const [to, setTo] = useState(searchParams.get('to') ?? '');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  // Round-trip filter → URL
  useEffect(() => {
    const next = new URLSearchParams();
    if (companyId) next.set('companyId', companyId);
    if (actorUserId) next.set('actorUserId', actorUserId);
    if (targetType) next.set('targetType', targetType);
    if (action) next.set('action', action);
    if (from) next.set('from', from);
    if (to) next.set('to', to);
    setSearchParams(next, { replace: true });
  }, [companyId, actorUserId, targetType, action, from, to, setSearchParams]);

  const filter: AuditLogFilter = useMemo(() => {
    const f: AuditLogFilter = {};
    if (companyId) f.companyId = companyId;
    if (actorUserId) f.actorUserId = actorUserId;
    if (targetType) f.targetType = targetType;
    if (action) f.action = action;
    if (from) {
      const fromIso = toIsoStart(from);
      if (fromIso) f.from = fromIso;
    }
    if (to) {
      const toIso = toIsoEnd(to);
      if (toIso) f.to = toIso;
    }
    if (cursor) f.cursor = cursor;
    return f;
  }, [companyId, actorUserId, targetType, action, from, to, cursor]);

  const { data, isLoading, isError } = useAuditLogs(filter);
  const { data: companiesData } = useCompanies();
  const { data: usersData } = useUsers();

  const entries = data?.entries ?? [];
  const companyNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of companiesData?.companies ?? []) m.set(c.id, c.name);
    return m;
  }, [companiesData]);
  const userEmailById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of usersData?.users ?? []) m.set(u.id, u.email);
    return m;
  }, [usersData]);

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function resetCursor() {
    setCursor(undefined);
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <header className="mb-6">
        <h2 className="text-2xl font-semibold leading-[1.3]">Audit log</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Chronological record of every admin action. Filters round-trip through the URL for
          sharing.
        </p>
      </header>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <div className="space-y-2">
          <Label htmlFor="al-company">Company</Label>
          <select
            id="al-company"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={companyId}
            onChange={(e) => {
              setCompanyId(e.target.value);
              resetCursor();
            }}
          >
            <option value="">All companies</option>
            {(companiesData?.companies ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="al-actor">Actor</Label>
          <select
            id="al-actor"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={actorUserId}
            onChange={(e) => {
              setActorUserId(e.target.value);
              resetCursor();
            }}
          >
            <option value="">All actors</option>
            {(usersData?.users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.email}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="al-target-type">Target type</Label>
          <input
            id="al-target-type"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={targetType}
            placeholder="e.g. Company"
            onChange={(e) => {
              setTargetType(e.target.value);
              resetCursor();
            }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="al-action">Action</Label>
          <input
            id="al-action"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={action}
            placeholder="e.g. company.archive"
            onChange={(e) => {
              setAction(e.target.value);
              resetCursor();
            }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="al-from">From</Label>
          <input
            id="al-from"
            type="date"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              resetCursor();
            }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="al-to">To</Label>
          <input
            id="al-to"
            type="date"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              resetCursor();
            }}
          />
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading audit log…</p>}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Couldn't load audit log.</AlertDescription>
        </Alert>
      )}

      {data && (
        <div className="rounded-md border bg-background overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">When</th>
                <th className="px-4 py-3 font-medium">Actor</th>
                <th className="px-4 py-3 font-medium">Company</th>
                <th className="px-4 py-3 font-medium">Action</th>
                <th className="px-4 py-3 font-medium">Target</th>
                <th className="px-4 py-3 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No entries match these filters.
                  </td>
                </tr>
              )}
              {entries.map((e) => {
                const isExpanded = expanded.has(e.id);
                const actorLabel = e.actorUserId
                  ? (userEmailById.get(e.actorUserId) ?? e.actorUserId)
                  : '(system)';
                const companyLabel = e.companyId
                  ? (companyNameById.get(e.companyId) ?? e.companyId)
                  : '—';
                const targetLabel = e.targetId ? `${e.targetType}/${e.targetId}` : e.targetType;
                return (
                  <Fragment key={e.id}>
                    <tr className="border-t align-top">
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {relativeTime(e.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{actorLabel}</td>
                      <td className="px-4 py-3 text-muted-foreground">{companyLabel}</td>
                      <td className="px-4 py-3 font-mono text-xs">{e.action}</td>
                      <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                        {targetLabel}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => toggle(e.id)}>
                          {isExpanded ? 'Collapse' : 'Expand'}
                        </Button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-t bg-muted/30">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                              <div className="text-xs font-semibold text-muted-foreground mb-1">
                                Before
                              </div>
                              <pre className="text-xs whitespace-pre-wrap break-all rounded bg-background border p-2">
                                {JSON.stringify(e.before, null, 2)}
                              </pre>
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-muted-foreground mb-1">
                                After
                              </div>
                              <pre className="text-xs whitespace-pre-wrap break-all rounded bg-background border p-2">
                                {JSON.stringify(e.after, null, 2)}
                              </pre>
                            </div>
                          </div>
                          {e.reason && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              Reason: {e.reason}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data?.nextCursor && (
        <div className="mt-4 flex justify-center">
          <Button variant="outline" onClick={() => setCursor(data.nextCursor ?? undefined)}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}

function toIsoStart(yyyymmdd: string): string | null {
  // yyyy-mm-dd → start of day UTC
  if (!/^\d{4}-\d{2}-\d{2}$/.test(yyyymmdd)) return null;
  return `${yyyymmdd}T00:00:00.000Z`;
}

function toIsoEnd(yyyymmdd: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(yyyymmdd)) return null;
  return `${yyyymmdd}T23:59:59.999Z`;
}
